/**
 * ConfigureButtonLinkDialog - Dialog de configuração do node "Botão Link" no Flow Builder
 * 
 * Permite configurar mensagem com botão CTA (Call-to-Action) clicável via Z-API.
 * Campos: Mensagem, Texto do Botão, URL, Título opcional, Rodapé opcional.
 * Inclui preview visual estilo WhatsApp.
 * 
 * @version 1.0.0
 * @date 2026-02-19
 */

import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { InputWithVariables } from '../InputWithVariables';
import { TextareaWithVariables } from '../TextareaWithVariables';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MousePointerClick } from 'lucide-react';
import { FlowNode } from '@/types/flows';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';

// =====================================================
// INTERFACES
// =====================================================

interface ConfigureButtonLinkDialogProps {
  /** Controla visibilidade do dialog */
  open: boolean;
  /** Callback para alterar visibilidade */
  onOpenChange: (open: boolean) => void;
  /** Node selecionado no canvas */
  node: FlowNode;
  /** Callback para salvar configuração do node */
  onSave: (nodeId: string, config: Record<string, any>) => void;
  /** ID do tenant para buscar variáveis disponíveis */
  tenantId: string;
}

// =====================================================
// COMPONENTE
// =====================================================

export function ConfigureButtonLinkDialog({
  open,
  onOpenChange,
  node,
  onSave,
  tenantId,
}: ConfigureButtonLinkDialogProps) {
  const [message, setMessage] = useState('');
  const [buttonLabel, setButtonLabel] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [title, setTitle] = useState('');
  const [footer, setFooter] = useState('');
  /** Ref para capturar apelido do node */
  const nicknameRef = useRef<NodeIdFieldRef>(null);

  // Carrega valores existentes ao abrir dialog
  useEffect(() => {
    if (open && node?.data?.config) {
      const config = node.data.config;
      setMessage(config.message || '');
      setButtonLabel(config.button_label || '');
      setLinkUrl(config.link_url || '');
      setTitle(config.title || '');
      setFooter(config.footer || '');
    } else if (open) {
      // Valores padrão para novo node
      setMessage('');
      setButtonLabel('Acessar');
      setLinkUrl('');
      setTitle('');
      setFooter('');
    }
  }, [open, node]);

  // Handler de salvar - persiste config no node
  const handleSave = () => {
    if (message.trim() && linkUrl.trim() && buttonLabel.trim()) {
      onSave(node.id, {
        ...node.data.config,
        message: message.trim(),
        button_label: buttonLabel.trim(),
        link_url: linkUrl.trim(),
        title: title.trim() || undefined,
        footer: footer.trim() || undefined,
        nickname: nicknameRef.current?.getNickname(),
      });
      onOpenChange(false);
    }
  };

  // Validação: mensagem, URL e label do botão são obrigatórios
  // Validação: aceita URL fixa (http) ou variável ({{)
  const isValid = message.trim().length > 0
    && (linkUrl.trim().startsWith('http') || linkUrl.trim().includes('{{'))
    && buttonLabel.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MousePointerClick className="h-5 w-5" />
            Configurar Botão Link
          </DialogTitle>
          <DialogDescription>
            Envia uma mensagem com um botão CTA clicável. O destinatário pode clicar no botão
            para abrir o link diretamente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Campo ID do Node e Apelido */}
          <NodeIdField ref={nicknameRef} nodeId={node.id} defaultNickname={node.data.config?.nickname} />
          
          {/* Título opcional (header) */}
          <div className="space-y-2">
            <Label htmlFor="cfg-btn-title">Título (opcional)</Label>
            <Input
              id="cfg-btn-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Título da mensagem"
              maxLength={60}
            />
          </div>

          {/* Mensagem principal - com botões de emoji e variáveis */}
          <div className="space-y-2">
            <Label htmlFor="cfg-btn-message">Mensagem *</Label>
            <TextareaWithVariables
              tenantId={tenantId}
              value={message}
              onChange={setMessage}
              placeholder="Olá {{contact_name}}, clique no botão abaixo para acessar:"
              rows={3}
            />
          </div>

          {/* Rodapé opcional */}
          <div className="space-y-2">
            <Label htmlFor="cfg-btn-footer">Rodapé (opcional)</Label>
            <Input
              id="cfg-btn-footer"
              value={footer}
              onChange={(e) => setFooter(e.target.value)}
              placeholder="Ex: Válido por 7 dias"
              maxLength={60}
            />
          </div>

          {/* URL do botão - suporta URL fixa ou variável */}
          <div className="space-y-2">
            <Label htmlFor="cfg-btn-link-url">URL do Botão *</Label>
            <InputWithVariables
              tenantId={tenantId}
              value={linkUrl}
              onChange={setLinkUrl}
              placeholder="https://exemplo.com ou {{scheduling_link}}"
            />
            <p className="text-xs text-muted-foreground">
              Informe uma URL fixa ou use o botão <code className="bg-muted px-1 rounded">{'{}'}</code> para inserir uma variável.
            </p>
          </div>

          {/* Texto do botão */}
          <div className="space-y-2">
            <Label htmlFor="cfg-btn-label">Texto do Botão *</Label>
            <Input
              id="cfg-btn-label"
              value={buttonLabel}
              onChange={(e) => setButtonLabel(e.target.value)}
              placeholder="Acessar Proposta"
              maxLength={25}
            />
            <p className="text-xs text-muted-foreground">
              Máximo 25 caracteres. Esse texto aparece no botão clicável.
            </p>
          </div>

          {/* Preview visual simulando WhatsApp */}
          {message && (
            <div className="p-3 border rounded-lg bg-muted/50 space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Preview</p>
              <div className="bg-card border rounded-lg p-3 max-w-[280px] space-y-2">
                {title && (
                  <p className="text-sm font-bold">{title}</p>
                )}
                <p className="text-sm whitespace-pre-wrap">{message}</p>
                {footer && (
                  <p className="text-xs text-muted-foreground">{footer}</p>
                )}
                <div className="border-t pt-2 mt-2">
                  <div className="flex items-center justify-center gap-2 text-primary text-sm font-medium py-1.5">
                    <MousePointerClick className="h-4 w-4" />
                    {buttonLabel || 'Acessar'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={!isValid}>
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
